import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  SelectGroup,
  SelectLabel,
  SelectSeparator,
} from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { useLocalePreferenceStore } from '@/stores/locale-preference'
import { REGIONS } from '@/lib/regions'
import { CURRENCIES } from '@/lib/currencies'

interface LocaleSettingsModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * "Update your settings" modal (spec-029). Region/Language/Currency are
 * staged locally and only committed on Save (FR-016) — Cancel/dismiss
 * discards them untouched. Language is committed via i18n.changeLanguage(),
 * never written to LocalePreferenceStore (see stores/locale-preference.ts).
 */
export function LocaleSettingsModal({ open, onOpenChange }: LocaleSettingsModalProps) {
  const { t, i18n } = useTranslation('navigation')
  const { region, currency, setRegion, setCurrency } = useLocalePreferenceStore()

  const [stagedRegion, setStagedRegion] = useState(region)
  const [stagedLanguage, setStagedLanguage] = useState(i18n.language)
  const [stagedCurrency, setStagedCurrency] = useState(currency)

  // Reset staged values to the current source of truth each time the modal opens —
  // never on every render, only on the closed→open transition.
  useEffect(() => {
    if (!open) return
    setStagedRegion(region)
    setStagedLanguage(i18n.language)
    setStagedCurrency(currency)
  }, [open, region, currency, i18n.language])

  function handleSave() {
    setRegion(stagedRegion)
    setCurrency(stagedCurrency)
    if (stagedLanguage !== i18n.language) {
      void i18n.changeLanguage(stagedLanguage)
    }
    onOpenChange(false)
  }

  function handleCancel() {
    onOpenChange(false)
  }

  // Radix Select's SelectValue only projects an item's label into the DOM
  // once that item has actually mounted (i.e. the popover has opened at
  // least once) — passing the resolved label explicitly avoids depending on
  // that side channel and keeps the trigger's displayed text correct on
  // first render.
  const regionLabel = t(REGIONS.find((r) => r.code.toUpperCase() === stagedRegion)?.nameKey ?? '')
  const languageLabel = t(`chrome.locale.languages.${stagedLanguage}`)
  const currencyLabel = t(CURRENCIES.find((c) => c.code === stagedCurrency)?.nameKey ?? '')

  // Top countries first (static, hand-curated — FR-006), then the rest
  // alphabetically by resolved display name. The "All Countries" group only
  // renders once a non-top-country entry actually exists (e.g. Uruguay) —
  // no code change needed here when that happens (SC-005).
  const topCountries = REGIONS.filter((r) => r.isTopCountry)
  const otherCountries = REGIONS.filter((r) => !r.isTopCountry).sort((a, b) =>
    t(a.nameKey).localeCompare(t(b.nameKey))
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('chrome.locale.modalTitle')}</DialogTitle>
          <DialogDescription>{t('chrome.locale.modalDescription')}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label id="locale-region-label" htmlFor="locale-region-trigger">{t('chrome.locale.regionLabel')}</Label>
            <Select value={stagedRegion} onValueChange={setStagedRegion}>
              <SelectTrigger id="locale-region-trigger" aria-labelledby="locale-region-label">
                <SelectValue>{regionLabel}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectLabel>{t('chrome.locale.topCountries')}</SelectLabel>
                  {topCountries.map((r) => (
                    <SelectItem key={r.code} value={r.code.toUpperCase()}>
                      {t(r.nameKey)}
                    </SelectItem>
                  ))}
                </SelectGroup>
                {otherCountries.length > 0 && (
                  <>
                    <SelectSeparator />
                    <SelectGroup>
                      <SelectLabel>{t('chrome.locale.allCountries')}</SelectLabel>
                      {otherCountries.map((r) => (
                        <SelectItem key={r.code} value={r.code.toUpperCase()}>
                          {t(r.nameKey)}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </>
                )}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label id="locale-language-label" htmlFor="locale-language-trigger">{t('chrome.locale.languageLabel')}</Label>
            <Select value={stagedLanguage} onValueChange={setStagedLanguage}>
              <SelectTrigger id="locale-language-trigger" aria-labelledby="locale-language-label">
                <SelectValue>{languageLabel}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="en">{t('chrome.locale.languages.en')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label id="locale-currency-label" htmlFor="locale-currency-trigger">{t('chrome.locale.currencyLabel')}</Label>
            <Select value={stagedCurrency} onValueChange={setStagedCurrency}>
              <SelectTrigger id="locale-currency-trigger" aria-labelledby="locale-currency-label">
                <SelectValue>{currencyLabel}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                {CURRENCIES.map((c) => (
                  <SelectItem key={c.code} value={c.code}>
                    {t(c.nameKey)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            {t('chrome.locale.cancel')}
          </Button>
          <Button onClick={handleSave}>{t('chrome.locale.save')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
